import { useState, useEffect } from 'react';
import { Undo2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { setHandedOut } from '@/services/tap.service.js';

// Bottom-sheet for logging who a keychain was handed to. Freeform for now
// (event attendee, FBO name, "Jeff at AOPA booth") — Phase 2 wires a Contact
// picker. Save stamps handed_out_at; an Undo affordance reverses the last
// hand-off in case of a misclick.
export default function HandOutSheet({ open, onOpenChange, token, onDone }) {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const alreadyHandedOut = !!token?.handed_out_at;

  useEffect(() => {
    if (open) setValue(token?.handed_out_to || '');
  }, [open, token]);

  async function save() {
    setSaving(true);
    try {
      await setHandedOut(token.id, { handed_out_to: value.trim() || null });
      toast.success(value.trim() ? `Handed out to ${value.trim()}` : 'Marked handed out');
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(`Hand-off failed: ${err.code || err.message}`);
    } finally {
      setSaving(false);
    }
  }

  async function undo() {
    setSaving(true);
    try {
      await setHandedOut(token.id, { undo: true });
      toast.success('Hand-off undone');
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(`Undo failed: ${err.code || err.message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="mx-auto max-w-lg">
        <SheetHeader>
          <SheetTitle>Hand out keychain</SheetTitle>
          <SheetDescription>
            …{token?.id?.slice(-8)} · capture who's taking this one.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4">
          <label className="mb-1.5 block text-xs font-medium text-text-muted" htmlFor="handout-to">
            Hand to
          </label>
          <Input
            id="handout-to"
            autoFocus
            placeholder="e.g. Jeff at AOPA booth"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !saving) save(); }}
          />
        </div>

        <SheetFooter className="mt-6">
          {alreadyHandedOut && (
            <Button variant="outline" onClick={undo} disabled={saving} className="sm:mr-auto">
              <Undo2 size={16} strokeWidth={1.5} /> Undo hand-off
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? 'Saving…' : alreadyHandedOut ? 'Update' : 'Mark handed out'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
